/**
 * Editor rich-text (TipTap) do conhecimento: toolbar fixa no topo do card
 * (botões ghost 32px, ativo bg-muted) + área ProseMirror com a mesma
 * tipografia manual usada na renderização do artigo.
 */
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  Bold,
  Italic,
  Link2,
  List,
  ListOrdered,
  Strikethrough,
  Undo2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { RICH_TEXT_CLASSES } from '@/features/knowledge/knowledge-article-view';

export function useKnowledgeEditor(initialHtml: string): Editor | null {
  return useEditor({
    extensions: [
      // StarterKit v3 já inclui Link (@tiptap/extension-link por baixo).
      StarterKit.configure({ link: { openOnClick: false, autolink: true } }),
    ],
    content: initialHtml,
    // Re-render a cada transaction para a toolbar refletir marks ativas.
    shouldRerenderOnTransaction: true,
  });
}

function ToolbarButton({
  label,
  active,
  disabled,
  onClick,
  children,
}: {
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-pressed={active}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault() /* não roubar o foco do editor */}
      onClick={onClick}
      className={cn(
        'flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:opacity-40',
        active && 'bg-muted text-foreground'
      )}
    >
      {children}
    </button>
  );
}

export function KnowledgeEditor({ editor }: { editor: Editor | null }) {
  if (!editor) return null;

  const setLink = () => {
    const previous = (editor.getAttributes('link').href as string | undefined) ?? '';
    // prompt() simples, conforme spec — string vazia remove o link.
    const url = window.prompt('URL do link', previous || 'https://');
    if (url === null) return;
    if (url.trim() === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run();
  };

  return (
    <div className="overflow-hidden rounded-lg border bg-card shadow-xs">
      {/* Toolbar fixa no topo do card */}
      <div className="flex items-center gap-0.5 border-b bg-card px-2 py-1.5">
        <ToolbarButton
          label="Negrito"
          active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <Bold className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          label="Itálico"
          active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          label="Tachado"
          active={editor.isActive('strike')}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        >
          <Strikethrough className="size-4" />
        </ToolbarButton>
        <span className="mx-1 h-5 w-px bg-border" aria-hidden />
        <ToolbarButton
          label="Lista com marcadores"
          active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <List className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          label="Lista numerada"
          active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered className="size-4" />
        </ToolbarButton>
        <span className="mx-1 h-5 w-px bg-border" aria-hidden />
        <ToolbarButton label="Link" active={editor.isActive('link')} onClick={setLink}>
          <Link2 className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          label="Desfazer"
          disabled={!editor.can().undo()}
          onClick={() => editor.chain().focus().undo().run()}
        >
          <Undo2 className="size-4" />
        </ToolbarButton>
      </div>

      {/* Área de edição */}
      <EditorContent
        editor={editor}
        className={cn(
          RICH_TEXT_CLASSES,
          '[&_.ProseMirror]:min-h-64 [&_.ProseMirror]:px-4 [&_.ProseMirror]:py-3 [&_.ProseMirror]:outline-none'
        )}
      />
    </div>
  );
}
